import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil } from 'lucide-react';
import { z } from 'zod';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { usePartsMarkup } from '@/hooks/usePartsMarkup';
import { effectiveSellPrice } from '@/utils/partsPricing';

const partSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  default_cost: z.number().positive('Cost must be positive').optional(),
  default_sell_price: z.number().min(0, 'Sell price cannot be negative').optional(),
});

type Part = {
  id: string;
  name: string;
  default_cost: number | null;
  default_sell_price: number | null;
  is_active: boolean;
};

export function PartsCatalog() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState<Part | null>(null);
  const [name, setName] = useState('');
  const [defaultCost, setDefaultCost] = useState('');
  const [defaultSell, setDefaultSell] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { locale, formatMoney } = useCompanyLocale();
  const markupPct = usePartsMarkup();

  const { data: parts = [], isLoading } = useQuery({
    queryKey: ['parts-catalog'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parts_catalog')
        .select('*')
        .order('name');
      if (error) throw error;
      return data as Part[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (values: z.infer<typeof partSchema>) => {
      const { error } = await supabase.from('parts_catalog').insert({
        name: values.name,
        default_cost: values.default_cost || null,
        default_sell_price: values.default_sell_price ?? null,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parts-catalog'] });
      toast({ title: 'Part created successfully' });
      resetForm();
    },
    onError: (error) => {
      toast({ title: 'Error creating part', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: z.infer<typeof partSchema> }) => {
      const { error } = await supabase
        .from('parts_catalog')
        .update({
          name: values.name,
          default_cost: values.default_cost || null,
          default_sell_price: values.default_sell_price ?? null,
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parts-catalog'] });
      toast({ title: 'Part updated successfully' });
      resetForm();
    },
    onError: (error) => {
      toast({ title: 'Error updating part', description: error.message, variant: 'destructive' });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('parts_catalog')
        .update({ is_active: !isActive })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parts-catalog'] });
      toast({ title: 'Part status updated' });
    },
  });

  const resetForm = () => {
    setName('');
    setDefaultCost('');
    setDefaultSell('');
    setEditingPart(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const validated = partSchema.parse({
        name,
        default_cost: defaultCost ? parseFloat(defaultCost) : undefined,
        default_sell_price: defaultSell ? parseFloat(defaultSell) : undefined,
      });
      if (editingPart) {
        updateMutation.mutate({ id: editingPart.id, values: validated });
      } else {
        createMutation.mutate(validated);
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({ title: 'Validation error', description: error.errors[0].message, variant: 'destructive' });
      }
    }
  };

  const handleEdit = (part: Part) => {
    setEditingPart(part);
    setName(part.name);
    setDefaultCost(part.default_cost?.toString() || '');
    setDefaultSell(part.default_sell_price?.toString() || '');
    setIsDialogOpen(true);
  };

  // Live preview of what a job will charge for this part with the current form values.
  const previewCost = defaultCost ? parseFloat(defaultCost) : null;
  const previewSell = defaultSell ? parseFloat(defaultSell) : null;
  const previewEffective =
    previewCost != null || previewSell != null
      ? effectiveSellPrice(previewCost, previewSell, markupPct)
      : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Parts</h3>
          <p className="text-sm text-muted-foreground">
            Manage commonly used parts and materials. Customers only ever see the sell price.
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Add Part
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingPart ? 'Edit Part' : 'New Part'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Part Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Garage Door Motor"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="defaultCost">Internal Cost ({locale.currency})</Label>
                  <Input
                    id="defaultCost"
                    type="number"
                    step="0.01"
                    min="0"
                    value={defaultCost}
                    onChange={(e) => setDefaultCost(e.target.value)}
                    placeholder="0.00"
                  />
                  <p className="text-xs text-muted-foreground">What you pay. Internal only.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="defaultSell">Sell Price ({locale.currency})</Label>
                  <Input
                    id="defaultSell"
                    type="number"
                    step="0.01"
                    min="0"
                    value={defaultSell}
                    onChange={(e) => setDefaultSell(e.target.value)}
                    placeholder={previewCost != null ? effectiveSellPrice(previewCost, null, markupPct).toFixed(2) : '0.00'}
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank to use cost + {markupPct}% markup.
                  </p>
                </div>
              </div>
              {previewEffective != null && (
                <p className="text-sm">
                  Customers will be charged <span className="font-semibold">{formatMoney(previewEffective)}</span>
                  {previewSell == null && ' (from markup)'}. Editable per job.
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingPart ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : parts.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No parts yet. Click "Add Part" to create one.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Part Name</TableHead>
              <TableHead>Internal Cost</TableHead>
              <TableHead>Sell Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {parts.map((part) => {
              const hasAnyPrice = part.default_cost != null || part.default_sell_price != null;
              return (
                <TableRow key={part.id}>
                  <TableCell className="font-medium">{part.name}</TableCell>
                  <TableCell>
                    {part.default_cost != null ? formatMoney(part.default_cost) : '-'}
                  </TableCell>
                  <TableCell>
                    {hasAnyPrice ? (
                      <span className="inline-flex items-center gap-2">
                        {formatMoney(effectiveSellPrice(part.default_cost, part.default_sell_price, markupPct))}
                        <Badge variant="outline" className="text-xs">
                          {part.default_sell_price != null ? 'fixed' : `${markupPct}% markup`}
                        </Badge>
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={part.is_active ? 'default' : 'secondary'}>
                      {part.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(part)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActiveMutation.mutate({ id: part.id, isActive: part.is_active })}
                      >
                        {part.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
